import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom-v5-compat";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {font, semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {useFlagOrCookie} from "@khan/analytics-growthbook";
import {
    EmailTextField,
    getErrorMessageFromUnknownError,
    isEmailValid,
    PASSWORD_MIN_LENGTH,
    PasswordTextField,
    signupUnderAgeGate,
    UsernameTextField,
} from "@khan/auth-password";
import {useGql, useQuery} from "@khan/data-gql";
import {KAError} from "@khan/logging";
import {standardTransition} from "@khan/shared-styles";
import {getContinueUrl, parseUrl, redirect, useLocation} from "@khan/urls";

import {BackLink} from "./back-link.tsx";
import {StandardSignupLegalBlurb} from "./legal-blurb.tsx";
import {SignUpLegalCheckbox} from "./signup-legal-checkbox.tsx";
import {SignupOauthButtons} from "./signup-oauth-buttons.tsx";
import {VALIDATE_CLASS_CODE_QUERY} from "./validate-class-code-query.ts";

import warningCircleIcon from "@phosphor-icons/core/bold/warning-circle-bold.svg";

interface Props {
    // Text which displays below the heading. Provides extra info to the user
    subHeading?: string;
    // The path to navigate to after the user has signed up
    nextPath: string;
    // The path for the back link
    backlinkPath: string;
    // The text for the back link
    backlinkText?: string;
    // Hide the back link or not
    hideBackLink?: boolean;
    // Maximum width of the component
    maxWidth?: string;
    // Callback triggered after a user has signed up, but before they are
    // redirected to the next page.
    onSubmitSuccess?: () => Promise<void>;
    // Whether to hide the parent email field
    hideParentField?: boolean;
    // If there isn't a continue= query param, use this path as the default
    defaultContinuePath?: string;
    // The class code to use for sign up.
    classCode?: string;
}

/**
 * A form for the U13 user to enter their username, parent email, and password
 * during sign up.
 *
 * Includes OAuth signup options (e.g., Google) when a valid classCode is provided.
 */
export const SignupUnderageForm = ({
    subHeading,
    nextPath,
    backlinkPath,
    backlinkText = t`Back`,
    maxWidth = "100%",
    hideBackLink = false,
    onSubmitSuccess,
    hideParentField = false,
    defaultContinuePath = "/",
    classCode = "",
}: Props) => {
    const [loading, setLoading] = useState(false);
    const [username, setUsername] = useState("");
    const [parentEmail, setParentEmail] = useState("");
    const [password, setPassword] = useState("");
    const [checked, setChecked] = useState(false);
    const [signupValidationError, setSignupValidationError] = useState("");
    const [oauthValidationError, setOauthValidationError] = useState("");
    const [oauthError, setOauthError] = useState("");
    const [signupError, setSignupError] = useState<string | React.ReactElement>(
        "",
    );
    const navigate = useNavigate();
    const location = useLocation();
    const gqlFetch = useGql();
    const tosCheckboxFeatureFlagOn = useFlagOrCookie(
        "signup-tos-checkbox-required",
    );

    const birthdate = useMemo(() => {
        return sessionStorage.getItem("new_signup_birthday") ?? "";
    }, []);

    // Query to validate classCode for OAuth button display
    const [classCodeQueryResult] = useQuery(VALIDATE_CLASS_CODE_QUERY, {
        variables: classCode ? {classCode} : undefined,
        fetchPolicy: FetchPolicy.CacheBeforeNetwork,
        skip: !classCode,
    });

    // Only show OAuth buttons if classCode is valid and query succeeded
    const showOAuthButtons =
        classCode &&
        classCodeQueryResult.status === "success" &&
        classCodeQueryResult.data?.joinByClassCodeInfoV2 != null;

    // When OAuth buttons are shown (valid classCode), hide parent email field
    // as the account is being created in association with a teacher
    const shouldHideParentField = hideParentField || showOAuthButtons;

    if (!birthdate) {
        navigate(backlinkPath);
        return;
    }

    const onUsernameChange = (value: string) => {
        setUsername(value);
    };

    const onParentEmailChange = (value: string) => {
        setParentEmail(value);
    };

    const onPasswordChange = (value: string) => {
        setPassword(value);
    };

    // Callback for SignupOauthButtons when TOS is not accepted
    const handleOAuthValidationError = () => {
        // Clear non oAuth errors
        setSignupError("");
        setSignupValidationError("");

        // Set TOS error
        setOauthValidationError(t`You must agree to the terms to proceed.`);
    };

    // Validates whether the user agreed to the terms
    const validateSignUpTerms = (): boolean => {
        // Skip validation if the TOS checkbox feature is turned off
        if (!tosCheckboxFeatureFlagOn) {
            return true;
        }
        if (!checked) {
            // Clear oAuth errors
            setOauthError("");
            setOauthValidationError("");

            // Set TOS error
            setSignupValidationError(
                t`You must agree to the terms to proceed.`,
            );
            return false;
        }

        // If form is valid clear previous form error
        setOauthValidationError("");
        setSignupValidationError("");
        return true;
    };

    const finalRedirectUrl = getContinueUrl(location) ?? defaultContinuePath;
    const continueUrl = parseUrl(nextPath);
    const backUrl = parseUrl(backlinkPath);
    if (finalRedirectUrl && finalRedirectUrl !== defaultContinuePath) {
        continueUrl.searchParams.set("continue", finalRedirectUrl);
        backUrl.searchParams.set("continue", finalRedirectUrl);
    }

    // Preserve classCode across redirects
    if (classCode && tosCheckboxFeatureFlagOn) {
        continueUrl.searchParams.set("classcode", classCode);
        backUrl.searchParams.set("classcode", classCode);
    }

    const onSignup = async () => {
        // Check if valid
        if (!validateSignUpTerms()) {
            return;
        }

        if (
            (!shouldHideParentField && !isEmailValid(parentEmail)) ||
            password.length < PASSWORD_MIN_LENGTH
        ) {
            setSignupError(t`Please fill out all required fields.`);
            return;
        }

        try {
            setLoading(true);
            await signupUnderAgeGate(gqlFetch, {
                // When hideParentField is true (valid classCode), pass empty string
                // as the account is being created in association with a teacher
                parentEmail: shouldHideParentField ? "" : parentEmail,
                password,
                username,
                birthdate,
                signupCodes: classCode ? [classCode] : undefined,
            });
            sessionStorage.removeItem("new_signup_birthday");
            if (onSubmitSuccess) {
                await onSubmitSuccess();
            }
            redirect(continueUrl.pathname + continueUrl.search);
        } catch (error) {
            if (error instanceof Error || error instanceof KAError) {
                const message = getErrorMessageFromUnknownError(
                    error,
                    t`Oops! Something went wrong. Please try again.`,
                );
                setSignupError(message);
            } else {
                setSignupError("An error occurred, please try again later.");
            }
        } finally {
            setLoading(false);
        }
    };

    const passwordDescription = t`Passwords should be at least ${PASSWORD_MIN_LENGTH} characters long and should contain a mixture of letters, numbers, and other characters.`;

    const usernameDescription = t`Use letters and numbers only. For safety, don't use your real name.`;

    return (
        <form
            action=""
            onSubmit={async (evt: React.SyntheticEvent): Promise<void> => {
                evt.preventDefault();
                await onSignup();
            }}
        >
            <View style={{maxWidth}}>
                {!hideBackLink && (
                    <>
                        <BackLink
                            text={backlinkText}
                            backUrl={backUrl.pathname + backUrl.search}
                            style={styles.backlink}
                        />
                    </>
                )}

                <View style={styles.fieldsContainer}>
                    <View style={styles.fieldsHeader}>
                        <HeadingSmall>{t`Sign up as a learner today!`}</HeadingSmall>
                        {subHeading && (
                            <LabelMedium style={styles.subHeading}>
                                {subHeading}
                            </LabelMedium>
                        )}
                    </View>

                    {tosCheckboxFeatureFlagOn && (
                        <SignUpLegalCheckbox
                            checked={checked}
                            setChecked={setChecked}
                            legalBlurb={<StandardSignupLegalBlurb />}
                        />
                    )}

                    {showOAuthButtons && (
                        <>
                            <ul className={css(styles.oauthButtons)}>
                                <SignupOauthButtons
                                    under13={true}
                                    role="learner"
                                    continueUrl={continueUrl.toString()}
                                    classCode={classCode}
                                    iconOnly={false}
                                    size="large"
                                    setServerError={setOauthError}
                                    showFacebookButton={false}
                                    birthdate={birthdate}
                                    validationRequired={
                                        tosCheckboxFeatureFlagOn
                                    }
                                    isFormValid={checked}
                                    onValidationError={
                                        handleOAuthValidationError
                                    }
                                />
                            </ul>
                            <View
                                role="alert"
                                aria-live="polite"
                                style={styles.errorMessageContainer}
                            >
                                {oauthValidationError && (
                                    <View style={styles.errorRow}>
                                        <PhosphorIcon
                                            icon={warningCircleIcon}
                                            color={
                                                semanticColor.status.critical
                                                    .foreground
                                            }
                                        />
                                        <Body
                                            tag="p"
                                            style={styles.errorMessage}
                                        >
                                            {oauthValidationError}
                                        </Body>
                                    </View>
                                )}
                                {oauthError && (
                                    <View style={styles.errorRow}>
                                        <PhosphorIcon
                                            icon={warningCircleIcon}
                                            color={
                                                semanticColor.status.critical
                                                    .foreground
                                            }
                                        />
                                        <Body
                                            tag="p"
                                            style={styles.errorMessage}
                                        >
                                            {oauthError}
                                        </Body>
                                    </View>
                                )}
                            </View>
                            <View style={styles.emailDivider}>
                                <Body
                                    tag="span"
                                    style={styles.emailDividerText}
                                >
                                    {t`Or sign up with email`}
                                </Body>
                            </View>
                        </>
                    )}

                    {!shouldHideParentField && (
                        <EmailTextField
                            required={true}
                            onChange={onParentEmailChange}
                            label={t`What is your parent or guardian's email?`}
                            description={t`You'll need permission to use Khan Academy`}
                            initialValue={parentEmail}
                        />
                    )}
                    <UsernameTextField
                        label={t`Choose a username`}
                        onChange={onUsernameChange}
                        value={username}
                        required={true}
                        description={usernameDescription}
                    />
                    <PasswordTextField
                        required={true}
                        label={t`Password`}
                        onChange={onPasswordChange}
                        initialValue={password}
                        description={passwordDescription}
                    />
                    <Button
                        type="submit"
                        style={styles.signupButton}
                        spinner={loading}
                    >
                        {t`Sign up`}
                    </Button>
                    <View role="alert" aria-live="polite">
                        {signupValidationError && (
                            <View style={styles.errorRow}>
                                <PhosphorIcon
                                    icon={warningCircleIcon}
                                    color={
                                        semanticColor.status.critical.foreground
                                    }
                                />
                                <Body tag="p" style={styles.errorMessage}>
                                    {signupValidationError}
                                </Body>
                            </View>
                        )}
                        {signupError && (
                            <View style={styles.errorRow}>
                                <PhosphorIcon
                                    icon={warningCircleIcon}
                                    color={
                                        semanticColor.status.critical.foreground
                                    }
                                />
                                <Body tag="p" style={styles.errorMessage}>
                                    {signupError}
                                </Body>
                            </View>
                        )}
                    </View>
                    {!tosCheckboxFeatureFlagOn && <StandardSignupLegalBlurb />}
                </View>
            </View>
        </form>
    );
};

const styles = StyleSheet.create({
    backlink: {
        alignSelf: "flex-start",
        marginBlockEnd: sizing.size_080,
    },
    fieldsHeader: {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
    },
    fieldsContainer: {
        gap: sizing.size_200,
        marginBlockEnd: sizing.size_160,
    },
    signupButton: {
        width: "100%",
    },
    subHeading: {
        color: semanticColor.core.foreground.neutral.subtle,
    },
    oauthButtons: {
        listStyle: "none",
        padding: 0,
        margin: 0,
        marginBlockEnd: sizing.size_160,
    },
    errorMessageContainer: {
        marginBlockEnd: sizing.size_320,
    },
    errorRow: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: sizing.size_080,
    },
    errorMessage: {
        color: semanticColor.status.critical.foreground,
        lineHeight: font.lineHeight.small,
        transition: standardTransition,
        transitionProperty: `max-height, opacity`,
        marginBlockStart: sizing.size_0,
        marginBlockEnd: sizing.size_0,
    },
    emailDivider: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        marginBlockEnd: sizing.size_160,
        ":after": {
            content: "''",
            position: "absolute",
            top: "50%",
            height: 1,
            width: "100%",
            backgroundColor: semanticColor.core.border.neutral.subtle,
        },
    },
    emailDividerText: {
        fontSize: font.size.small,
        backgroundColor: semanticColor.core.background.base.default,
        padding: `0 ${sizing.size_100}`,
        position: "relative",
        zIndex: 1,
    },
});
